import React, { useState } from 'react';
import { 
  ArrowLeft, 
  Sun, 
  Zap, 
  CheckCircle2, 
  Smartphone, 
  FileSpreadsheet, 
  FileCheck, 
  WifiOff, 
  Clock, 
  Award,
  ChevronRight,
  Calculator,
  AlertCircle
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';

const JustSolar = () => {
  const navigate = useNavigate();
  const [activeFaq, setActiveFaq] = useState(null);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const faqs = [
    {
      q: "What devices are supported by JustSolar?",
      a: "JustSolar is engineered as a native Android application and runs smoothly on all modern Android devices running Android 9.0 (Pie) and higher, including Android smartphones and tablets."
    },
    {
      q: "Can I use JustSolar without an active internet connection?",
      a: "Yes! JustSolar is built offline-first. You can perform complete site assessments, rooftop shading calculations, and BOM exports directly in remote or rural locations without mobile reception. When you reconnect, you can email or backup your proposals."
    },
    {
      q: "How does the lifetime license work?",
      a: "You pay a one-time fee of ₹1,990. There are no recurring monthly or annual subscription charges. Your single-device license remains active for lifetime access, including free maintenance updates and bug fixes."
    },
    {
      q: "Is activation instant upon payment?",
      a: "No. License activation is NOT instant or automated. Upon completing your payment, our engineering team manually verifies your transaction, registers your company name, and provisions your unique license key within 2 to 24 business hours. You will receive an email with your key and APK download link."
    },
    {
      q: "What is your refund policy for JustSolar?",
      a: "We offer a 7-day technical satisfaction guarantee. If the application has unresolved technical incompatibility on your supported device within 7 days of purchase, we initiate a 100% refund. Please review our Refund Policy page for full terms."
    }
  ];

  return (
    <div className="justsolar-page">
      {/* Top Navigation */}
      <header className="justsolar-header">
        <div className="container justsolar-nav-container">
          <button className="back-button" onClick={() => navigate('/')}>
            <ArrowLeft size={18} />
            Back to JustLabs Home
          </button>
          <div className="justsolar-brand-badge">
            <Sun size={20} className="sun-icon-spin" />
            <span>JustSolar by JustLabs</span>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="justsolar-hero">
        <div className="container">
          <div className="justsolar-hero-content">
            <div className="hero-status-pill">
              <span className="pulsing-dot"></span>
              Launching Soon • Early-Bird Lifetime License
            </div>

            <h1 className="justsolar-title">
              The Dedicated Android EPC Suite for Solar Businesses
            </h1>

            <p className="justsolar-subtitle">
              JustSolar is a paid commercial Android software product engineered specifically for 
              <strong> Solar EPC contractors, rooftop installers, and site survey engineers</strong>. 
              Accelerate rooftop surveys, calculate PV capacity, generate instant Bill of Materials (BOM), 
              and produce professional client proposals right on your Android phone or tablet.
            </p>

            {/* Quick Highlights Grid */}
            <div className="hero-highlights">
              <div className="highlight-pill">
                <CheckCircle2 size={16} /> 100% Offline Capable
              </div>
              <div className="highlight-pill">
                <CheckCircle2 size={16} /> Native Android Performance
              </div>
              <div className="highlight-pill">
                <CheckCircle2 size={16} /> No Monthly Subscriptions
              </div>
              <div className="highlight-pill">
                <CheckCircle2 size={16} /> Instant Branded Proposal PDFs
              </div>
            </div>

            <div className="justsolar-hero-actions">
              <a href="#pricing" className="btn-primary-solar">
                <Sun size={20} />
                Lifetime Access (₹1,990)
              </a>
              <a href="#delivery-workflow" className="btn-secondary-solar">
                <Clock size={20} />
                How Payment & Activation Works
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Who JustSolar Is Built For */}
      <section className="section solar-audience-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Target Users</span>
            <h2 className="section-title">Built for Solar Field Professionals</h2>
            <p className="section-subtitle">
              Designed from the ground up to solve daily operational bottlenecks for solar engineering and installation businesses.
            </p>
          </div>

          <div className="audience-grid">
            <div className="audience-card">
              <div className="audience-icon">
                <Award size={32} />
              </div>
              <h3>Solar EPC Contractors</h3>
              <p>
                Standardize field survey inspections, eliminate manual spreadsheet calculation errors, and speed up project turnaround times from initial survey to installation handover.
              </p>
            </div>

            <div className="audience-card">
              <div className="audience-icon">
                <Sun size={32} />
              </div>
              <h3>Rooftop Solar Installers</h3>
              <p>
                Survey residential and commercial rooftops, evaluate shadow obstructions, and present clients with accurate sizing and equipment proposals on the spot.
              </p>
            </div>

            <div className="audience-card">
              <div className="audience-icon">
                <Calculator size={32} />
              </div>
              <h3>Field Engineers & Surveyors</h3>
              <p>
                Perform rapid solar capacity feasibility assessments in remote rural areas without requiring bulky laptops or active internet connectivity.
              </p>
            </div>

            <div className="audience-card">
              <div className="audience-icon">
                <FileSpreadsheet size={32} />
              </div>
              <h3>Solar Sales Consultants</h3>
              <p>
                Deliver transparent, branded quotations with accurate Bill of Materials (BOM) during the first customer meeting to increase conversion rates.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="section solar-features-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Core Capabilities</span>
            <h2 className="section-title">Everything You Need In The Field</h2>
            <p className="section-subtitle">
              A comprehensive toolkit tailored to Indian and international solar installation workflows.
            </p>
          </div>

          <div className="features-solar-grid">
            <div className="feature-solar-card">
              <div className="feature-solar-icon"><Sun size={28} /></div>
              <h4>Solar PV Capacity Calculator</h4>
              <p>
                Calculate optimal system capacity (kW/MW) based on available rooftop shadow-free area, monthly electricity consumption, or budget constraints.
              </p>
            </div>

            <div className="feature-solar-card">
              <div className="feature-solar-icon"><Zap size={28} /></div>
              <h4>Module & Inverter Matching</h4>
              <p>
                Automate string sizing, panel configurations, and inverter selection compatible with mono-PERC, TOPCon, and bifacial solar modules.
              </p>
            </div>

            <div className="feature-solar-card">
              <div className="feature-solar-icon"><FileSpreadsheet size={28} /></div>
              <h4>Dynamic Bill of Materials (BOM)</h4>
              <p>
                Generate comprehensive material schedules covering modules, inverters, mounting structures, ACDB/DCDB, cables, earthing, and lightning arrestors.
              </p>
            </div>

            <div className="feature-solar-card">
              <div className="feature-solar-icon"><FileCheck size={28} /></div>
              <h4>Instant PDF Client Proposals</h4>
              <p>
                Export professionally styled client proposals complete with your company logo, system specifications, financial return estimates, and terms.
              </p>
            </div>

            <div className="feature-solar-card">
              <div className="feature-solar-icon"><WifiOff size={28} /></div>
              <h4>100% Offline Capability</h4>
              <p>
                Perform all site surveys and sizing offline. No internet required on field sites; your data is stored locally and securely on your Android device.
              </p>
            </div>

            <div className="feature-solar-card">
              <div className="feature-solar-icon"><Smartphone size={28} /></div>
              <h4>Optimized for Android</h4>
              <p>
                Fluid, responsive user interface built using modern Android principles, optimized for both pocket smartphones and large survey tablets.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="section solar-pricing-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Transparent Pricing</span>
            <h2 className="section-title">One-Time Lifetime Access License</h2>
            <p className="section-subtitle">
              No monthly subscriptions. No per-quote fees. Own your solar engineering tool outright.
            </p>
          </div>

          <div className="pricing-wrapper">
            <div className="pricing-card featured-pricing">
              <div className="pricing-badge-popular">Early-Bird Lifetime Access</div>
              
              <div className="pricing-header">
                <h3>JustSolar Commercial License</h3>
                <p>Full-featured Android software for Solar EPC businesses</p>
              </div>

              <div className="price-display">
                <span className="currency">₹</span>
                <span className="amount">1,990</span>
                <span className="tenure">One-Time Payment</span>
              </div>
              <div className="price-sub-note">
                <span>Inclusive of all taxes • Single-Device Lifetime License</span>
              </div>

              <div className="pricing-divider"></div>

              <ul className="pricing-perks">
                <li><CheckCircle2 size={18} className="perk-check" /> 1 Lifetime Single-Device Android License</li>
                <li><CheckCircle2 size={18} className="perk-check" /> Unlimited Site Surveys & Capacity Calculations</li>
                <li><CheckCircle2 size={18} className="perk-check" /> Unlimited Bill of Materials (BOM) Generation</li>
                <li><CheckCircle2 size={18} className="perk-check" /> Unlimited Branded PDF Proposal Exports</li>
                <li><CheckCircle2 size={18} className="perk-check" /> 100% Offline Access for Remote Site Surveys</li>
                <li><CheckCircle2 size={18} className="perk-check" /> Free Lifetime Software Updates & Bug Fixes</li>
                <li><CheckCircle2 size={18} className="perk-check" /> Dedicated Email Support (pankajacharyaorg@gmail.com)</li>
                <li><CheckCircle2 size={18} className="perk-check" /> 7-Day Technical Satisfaction Guarantee</li>
              </ul>

              {/* Maintenance Status Action Area */}
              <div className="checkout-action-area">
                <div className="maintenance-action-box">
                  <button className="buy-solar-btn btn-under-maintenance" disabled>
                    <Clock size={20} />
                    <span>Under Maintenance</span>
                  </button>
                  
                  <div className="maintenance-note-card">
                    <AlertCircle size={18} className="maintenance-alert-icon" />
                    <p>
                      <strong>License Checkout Under Maintenance:</strong> Online direct payment is temporarily undergoing scheduled maintenance. To purchase a license or for early-access activation, please write directly to <a href="mailto:pankajacharyaorg@gmail.com">pankajacharyaorg@gmail.com</a>.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Payment and Delivery Workflow */}
      <section id="delivery-workflow" className="section delivery-workflow-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Transparency First</span>
            <h2 className="section-title">How Delivery & Activation Works</h2>
            <p className="section-subtitle">
              We believe in total honesty: <strong>Activation is not instant or automatic</strong>. Here is the exact step-by-step process of what happens after your payment.
            </p>
          </div>

          <div className="workflow-timeline">
            <div className="workflow-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h4>Order Confirmation & Payment</h4>
                <p>
                  You complete the one-time payment of <strong>₹1,990</strong> through our secure checkout using UPI, Net Banking, or Credit/Debit Cards.
                </p>
              </div>
            </div>

            <div className="workflow-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h4>Official Payment Receipt</h4>
                <p>
                  Upon successful payment, an official transaction receipt with your unique Payment ID is automatically dispatched to your registered email address.
                </p>
              </div>
            </div>

            <div className="workflow-step step-highlight">
              <div className="step-number">3</div>
              <div className="step-content">
                <h4>Manual Review & License Provisioning (2 to 24 Business Hours)</h4>
                <p>
                  <strong>Notice:</strong> License activation is <strong>NOT instant or automatic</strong>. Our technical support team manually reviews each incoming transaction, registers your business details in our licensing registry, and generates a unique, cryptographically signed JustSolar lifetime license key.
                </p>
                <div className="time-badge">
                  <Clock size={16} /> Processing Time: 2 to 24 Business Hours
                </div>
              </div>
            </div>

            <div className="workflow-step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h4>Email Delivery of APK & Activation Key</h4>
                <p>
                  You will receive an official onboarding email from <code>pankajacharyaorg@gmail.com</code> containing:
                </p>
                <ul className="step-deliverables-list">
                  <li><strong>Your Unique JustSolar Lifetime Activation Key</strong></li>
                  <li><strong>Secure Download Link</strong> for the official JustSolar Android APK</li>
                  <li><strong>Quick-Start Guide</strong> for setting up solar pricing and company branding</li>
                </ul>
              </div>
            </div>

            <div className="workflow-step">
              <div className="step-number">5</div>
              <div className="step-content">
                <h4>Install on Android & Begin Sizing</h4>
                <p>
                  Install the JustSolar APK on your Android phone or tablet (Android 9.0+), enter your activation key during initial startup, and unlock full lifetime access. If you ever need help or change devices, our direct support is always available at <code>pankajacharyaorg@gmail.com</code>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Frequently Asked Questions */}
      <section className="section solar-faq-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">FAQ</span>
            <h2 className="section-title">Frequently Asked Questions</h2>
          </div>

          <div className="faq-accordion">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className={`faq-card ${activeFaq === index ? 'active' : ''}`}
                onClick={() => toggleFaq(index)}
              >
                <div className="faq-question">
                  <h4>{faq.q}</h4>
                  <ChevronRight size={20} className="faq-chevron" />
                </div>
                {activeFaq === index && (
                  <div className="faq-answer">
                    <p>{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="faq-compliance-links">
            <p>Need more details before purchasing? Review our accessible policies:</p>
            <div className="policy-chips">
              <Link to="/terms-and-conditions">Terms & Conditions</Link>
              <Link to="/refund-cancellation-policy">Refund & Cancellation Policy</Link>
              <Link to="/justsolar_privacypolicy">JustSolar Privacy Policy</Link>
              <Link to="/contact-us">Contact Us & Merchant Details</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Merchant / Legal Info Banner */}
      <section className="solar-merchant-footer">
        <div className="container">
          <div className="merchant-footer-content">
            <p>
              <strong>Legal Seller Information:</strong> JustSolar is developed, sold, and supported by 
              <strong> Pankaj Acharya</strong> (Sole Proprietor, trading as <strong>JustLabs</strong>).
            </p>
            <p>
              Support Email: <a href="mailto:pankajacharyaorg@gmail.com">pankajacharyaorg@gmail.com</a> • 
              Website: <a href="https://justlabs.in">justlabs.in</a> • India
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default JustSolar;
